import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { BrowserModule } from '@angular/platform-browser';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { JwtModule } from '@auth0/angular-jwt';
import { VtrCalendarModule } from '@vitralogy/ng-calendar';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DocumentViewerComponent } from './components/document-viewer/document-viewer.component';
import { DocumentsService } from './services/sections/documents/documents.service';
import { DocumentListComponent } from './components/document-list/document-list.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { NavigationBarComponent } from './components/navigation-bar/navigation-bar.component';
import { JobDetailsComponent } from './components/job-details/job-details.component';
import { ChangeStatusComponent } from './components/job-details/change-status/change-status.component';
import { LoaderComponent } from './components/loader/loader.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { FingerprintInterceptor } from './interceptors/fingerprint.interceptor';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastComponent } from './components/toast/toast.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { ChangeEtaComponent } from './components/job-details/change-eta/change-eta.component';
import { CommentFollowUpComponent } from './components/job-details/comment-follow-up/comment-follow-up.component';
import { AddDocumentsComponent } from './components/job-details/add-documents/add-documents.component';
import { AssigneesComponent } from './components/job-details/assignees/assignees.component';
import { RequestUpdateComponent } from './components/job-details/request-update/request-update.component';
import { ActionButtonComponent } from './components/action-button/action-button.component';
import { MatchContactsComponent } from './components/job-details/match-contacts/match-contacts.component';
import { FollowUpEditComponent } from './components/follow-up-details/follow-up-edit/follow-up-edit.component';
import { FollowUpDetailsComponent } from './components/follow-up-details/follow-up-details.component';
import { NoteThreadComponent } from './components/job-details/note-thread/note-thread.component';
import { GenericModalComponent } from './components/generic-modal/generic-modal.component';
import { ContactsListComponent } from './components/job-details/contacts-list/contacts-list.component';
import { BoardProposalsComponent } from './components/proposal-details/proposal-details.component';
import { BoardProposalFormComponent } from './components/job-details/board-proposal-form/board-proposal-form.component';
import { ProposalsQueueComponent } from './components/job-details/proposals-queue/proposals-queue.component';
import { DocumentSelectionComponent } from './components/document-selection/document-selection.component';
import { LongLeftClickDirective } from './directives/long-left-click.directive';
import { ProposalNoteThreadComponent } from './components/proposal-details/proposal-note-thread/proposal-note-thread.component';
import { NgxCurrencyModule } from 'ngx-currency';
import { FormsComponent } from './components/forms/forms.component';
import { PreferencesModalComponent } from './components/navigation-bar/templates/preferences-modal/preferences-modal.component';
import { FormViewerComponent } from './components/form-viewer/form-viewer.component';
import { ActionButtonCarouselComponent } from './components/action-button-carousel/action-button-carousel.component';
import { MyCloudComponent } from './components/my-cloud/my-cloud.component';
import { PlainInfoComponent } from './components/plain-info/plain-info.component';
import { MyWorkComponent } from './components/job-details/my-work/my-work.component';
import { DigitalLogFormComponent } from './components/job-details/digital-log/form/digital-log-form.component';
import { DigitalLogFormPhotosComponent } from './components/job-details/digital-log/form/photos/digital-log-form-photos.component';
import { DigitalLogModule } from '@vitralogy/ng-digital-log';
import { DigitalLogFormRequestsComponent } from './components/job-details/digital-log/form-requests/digital-log-form-requests.component';
import { DigitalLogFormCalendarComponent } from './components/job-details/digital-log/form-calendar/digital-log-form-calendar.component';
import { LegendComponent } from './components/legend/legend.component';
import { LoginWizardComponent } from './components/login-wizard/login-wizard.component';
import { VirtualBinderWizardComponent } from './components/virtual-binder-wizard/virtual-binder-wizard.component';
import { VirtualBinderConfirmationComponent } from './components/virtual-binder-wizard/virtual-binder-confirmation/virtual-binder-confirmation.component';
import { LoginCallbackComponent } from './components/login-wizard/login-callback/login-callback.component';
import { AccountsListComponent } from './components/login-wizard/accounts-list/accounts-list.component';
import { VirtualBinderComponent } from './components/virtual-binder/virtual-binder.component';
import { ExpiredVirtualBinderComponent } from './components/virtual-binder/expired-virtual-binder/expired-virtual-binder.component';
import { SiteNotesComponent } from './components/site-notes/site-notes.component';
import { DeficienciesComponent } from './components/deficiencies/deficiencies.component';
import { SiteNoteViewComponent } from './components/site-notes/site-note-view/site-note-view.component';
import { SiteNoteAddComponent } from './components/site-notes/site-note-add/site-note-add.component';
import { SiteNotePhotosComponent } from './components/site-notes/site-note-photos/site-note-photos.component';
import { DeficiencyViewComponent } from './components/deficiencies/deficiency-view/deficiency-view.component';
import { AuthService } from './services/auth.service';
import { DigitalLogbookComponent } from './components/virtual-binder/digital-logbook/digital-logbook.component';
import {MatRadioModule} from '@angular/material/radio';
import { DigitalLogOnDemandAddComponent } from './components/job-details/digital-log/digital-log-on-demand-add/digital-log-on-demand-add.component';
import { TermsOfUseComponent } from './components/terms-of-use/terms-of-use.component';
import { BinderSendDocumentationComponent } from './components/virtual-binder/binder-send-documentation/binder-send-documentation.component';
import { BinderQrScanHistoryComponent } from './components/virtual-binder/binder-qr-scan-history/binder-qr-scan-history.component';
import { MatTableModule } from '@angular/material/table';
import { DocumentViewPageComponent } from './components/document-view-page/document-view-page.component';
import { EditBinderComponent } from './components/virtual-binder/edit-binder/edit-binder.component';
import {MatBadgeModule} from '@angular/material/badge';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatTooltipModule} from '@angular/material/tooltip';
import { DisabledVirtualBinderComponent } from './components/virtual-binder/disabled-virtual-binder/disabled-virtual-binder.component';
import { SiteNoteAddTemplateComponent } from './components/site-notes/site-note-add-template/site-note-add-template.component';
import { SiteNoteCompletionComponent } from './components/site-notes/site-note-completion/site-note-completion.component';
import { InspectionReportSelectorComponent } from './components/job-details/digital-log/form-requests/inspection-report-selector/inspection-report-selector.component';
import { WorkforceReportComponent } from './components/workforce-report/workforce-report.component';
import { WorkforceReportBysiteComponent } from './components/workforce-report-bysite/workforce-report-bysite.component';
import { DlFormDocumentsComponent } from './components/dl-form-documents/dl-form-documents.component';
import { MatTreeModule } from '@angular/material/tree';
import { OnDemandRequestProgressComponent } from './components/job-details/digital-log/form-requests/on-demand-request-progress/on-demand-request-progress.component';
import { HelpRequestModalComponent } from './components/navigation-bar/help-request-modal/help-request-modal.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { BinderAccessComponent } from './components/virtual-binder/binder-access/binder-access.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MapsService } from './services/address/maps.service';
import { FieldsAttachmentsListComponent } from './components/job-details/digital-log/form-requests/fields-attachments-list/fields-attachments-list.component';
import { UnregisterComponent } from './components/unregister/unregister.component';
import { DeleteUserAccountComponent } from './components/unregister/delete-account/delete-user-account.component';
import { ProcedureFormBypassComponent } from './components/procedure-form-bypass/procedure-form-bypass.component';
import { ProcedureFormValidationsSkipComponent } from './components/procedure-form-validations-skip/procedure-form-validations-skip.component';
import { RejectBypassRequestComponent } from './components/procedure-form-bypass/reject-bypass-request/reject-bypass-request.component';
import { RejectValidationsSkipRequestComponent } from './components/procedure-form-validations-skip/reject-validations-skip-request/reject-validations-skip-request.component';
import { BinderSessionComponent } from './components/virtual-binder/binder-session/binder-session.component';
import { BinderSessionConfirmationComponent } from './components/virtual-binder/binder-session/binder-session-confirmation/binder-session-confirmation.component';
import { BinderSessionCodeComponent } from './components/virtual-binder/binder-session/binder-session-code/binder-session-code.component';
import { BinderSessionStatusComponent } from './components/virtual-binder/binder-session/binder-session-status/binder-session-status.component';
import { BinderViewerComponent } from './components/virtual-binder/binder-viewer/binder-viewer.component';
import { ProcedurePreApprovalComponent } from './components/procedure-pre-approval/procedure-pre-approval.component';
import { RejectPreApprovalRequestComponent } from './components/procedure-pre-approval/reject-pre-approval-request/reject-pre-approval-request.component';
import { DynamicControlsModule } from './components/job-details/dynamic-controls/dynamic-controls.module';
import { ProcedureCopilotConfirmationComponent } from './components/procedure-copilot-confirmation/procedure-copilot-confirmation.component';
import { ProcedureCopilotConfirmationExpiredComponent } from './components/procedure-copilot-confirmation-expired/procedure-copilot-confirmation-expired.component';
import { StepPhotosComponent } from './components/procedure-copilot-confirmation/step-photos/step-photos.component';
import { CopilotConfirmationProceduresListComponent } from './components/copilot-confirmation-procedures-list/copilot-confirmation-procedures-list.component';
import { ProcedureStepFormDetailsComponent } from './components/procedure-step-form-details/procedure-step-form-details.component';
import { ProcedureSummaryReviewComponent } from './components/procedure-summary-review/procedure-summary-review.component';
import { ProcedureSummaryReviewExpiredComponent } from './components/procedure-summary-review-expired/procedure-summary-review-expired.component';

@NgModule({
	declarations: [
		AppComponent,
		DocumentViewerComponent,
		DocumentListComponent,
		NavigationBarComponent,
		LoaderComponent,
		JobDetailsComponent,
		ChangeStatusComponent,
		ChangeEtaComponent,
		AddDocumentsComponent,
		CommentFollowUpComponent,
		FollowUpEditComponent,
		FollowUpDetailsComponent,
		NotFoundComponent,
		ToastComponent,
		AssigneesComponent,
		RequestUpdateComponent,
		ActionButtonComponent,
		MatchContactsComponent,
		NoteThreadComponent,
		GenericModalComponent,
		ContactsListComponent,
		BoardProposalsComponent,
		BoardProposalFormComponent,
		ProposalsQueueComponent,
		DocumentSelectionComponent,
		LongLeftClickDirective,
		ProposalNoteThreadComponent,
		FormsComponent,
		PreferencesModalComponent,
		FormViewerComponent,
		ActionButtonCarouselComponent,
		MyCloudComponent,
		MyWorkComponent,
		PlainInfoComponent,
		DigitalLogFormComponent,
		DigitalLogFormPhotosComponent,
		DigitalLogFormRequestsComponent,
		DigitalLogFormCalendarComponent,
		LegendComponent,
		LoginWizardComponent,
		LoginCallbackComponent,
		AccountsListComponent,
		VirtualBinderWizardComponent,
		VirtualBinderConfirmationComponent,
		VirtualBinderComponent,
		ExpiredVirtualBinderComponent,
		SiteNotesComponent,
		SiteNoteViewComponent,
		DeficienciesComponent,
		SiteNoteAddComponent,
		SiteNotePhotosComponent,
		DeficiencyViewComponent,
		DigitalLogbookComponent,
		DigitalLogOnDemandAddComponent,
		TermsOfUseComponent,
  		BinderSendDocumentationComponent,
  		BinderQrScanHistoryComponent,
    	DocumentViewPageComponent,
		EditBinderComponent,
		DisabledVirtualBinderComponent,
		SiteNoteAddTemplateComponent,
		SiteNoteCompletionComponent,
		InspectionReportSelectorComponent,
  		WorkforceReportComponent,
		WorkforceReportBysiteComponent,
 		DlFormDocumentsComponent,
 		OnDemandRequestProgressComponent,
		HelpRequestModalComponent,
		BinderAccessComponent,
		FieldsAttachmentsListComponent,
		UnregisterComponent,
		DeleteUserAccountComponent,
  		ProcedureFormBypassComponent,
		ProcedureFormValidationsSkipComponent,
    	RejectBypassRequestComponent,
		RejectValidationsSkipRequestComponent,
		BinderSessionComponent,
		BinderSessionConfirmationComponent,
		BinderSessionCodeComponent,
  		BinderSessionStatusComponent,
		BinderViewerComponent,
  		ProcedurePreApprovalComponent,
  		RejectPreApprovalRequestComponent,
    	ProcedureCopilotConfirmationComponent,
		ProcedureCopilotConfirmationExpiredComponent,
		StepPhotosComponent,
		CopilotConfirmationProceduresListComponent,
		ProcedureStepFormDetailsComponent,
  ProcedureSummaryReviewComponent,
  ProcedureSummaryReviewExpiredComponent
	],
	imports: [
		BrowserModule,
		AppRoutingModule,
		HttpClientModule,
		FormsModule,
		ReactiveFormsModule,
		BrowserAnimationsModule,
		NgxCurrencyModule,
		MatCardModule,
		MatSidenavModule,
		MatTabsModule,
		MatButtonToggleModule,
		MatIconModule,
		MatButtonModule,
		MatListModule,
		MatDialogModule,
		MatInputModule,
		MatFormFieldModule,
		MatNativeDateModule,
		MatDatepickerModule,
		MatMenuModule,
		MatSelectModule,
		MatCheckboxModule,
		DigitalLogModule,
		VtrCalendarModule,
		ClipboardModule,
		MatRadioModule,
		MatTableModule,
		MatBadgeModule,
		MatExpansionModule,
		MatTooltipModule,
		MatTreeModule,
		MatSlideToggleModule,
		MatAutocompleteModule,
		MatProgressSpinnerModule,
		MatProgressBarModule,
		DynamicControlsModule,
		JwtModule.forRoot({
			config: {
				tokenGetter: () => localStorage.getItem(AuthService.VtrTokenKey),
				throwNoTokenError: false
			}
		})
	],
	providers: [
		DocumentsService,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: FingerprintInterceptor,
			multi: true
		}
	],
	bootstrap: [AppComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
